'use client';

import { signOut } from '@/lib/auth/auth-client';
import { isPublicPathname } from '@/lib/auth/public-routes';

/**
 * Login URL used when recovering from a dead/absent session. Mirrors the
 * existing `archicad-bridge` recovery flow, which redirects to
 * `/login?message=archicad-session-expired`. Here the trigger is a generic
 * expired app session rather than a plugin-specific one. See ENG-1242.
 */
export const SESSION_EXPIRED_LOGIN_URL = '/login?message=session-expired';

/**
 * True when the current page is reachable without a session, so a 401 there
 * must NOT trigger a redirect-to-login (it would sign out an anonymous visitor
 * and yank them off public content, or create a redirect loop). Uses the same
 * `isPublicPathname` list the middleware proxy enforces, plus the auth-adjacent
 * routes that require a session but are still part of the login/verification
 * flow (`/verify-email`, `/error`).
 */
function isRecoverySkippedPathname(pathname: string): boolean {
  return (
    isPublicPathname(pathname) ||
    pathname === '/verify-email' ||
    pathname.startsWith('/error')
  );
}

/**
 * Host-bridge execution-polling endpoints (Rhino, Archicad) authenticate with a
 * SEPARATE short-lived bearer token and run their OWN in-place reauth when that
 * token expires — see `hooks/use-rhino-stream.ts` /
 * `hooks/use-archicad-stream.ts` and the matching `executions/auth.ts` routes,
 * which prioritize the bearer token and do NOT fall back to the app session. A
 * 401 from these endpoints therefore means the BRIDGE token died, not the app
 * session, so global recovery must not run (it would wrongly sign the user out
 * and race the bridge's own recovery). Revit's poll uses the app-session cookie
 * (the shared `fetcher`), so its 401s are genuine and intentionally excluded
 * from this list.
 */
const BRIDGE_TOKEN_ENDPOINT_PREFIXES = [
  '/api/rhino/executions',
  '/api/archicad/executions',
];

/**
 * True when the failing SWR key is a host-bridge bearer-token endpoint (see
 * {@link BRIDGE_TOKEN_ENDPOINT_PREFIXES}). Only string keys are matched; a 401
 * from anything else is treated as a candidate dead-session signal.
 */
export function isBridgeTokenEndpoint(key: unknown): boolean {
  if (typeof key !== 'string') {
    return false;
  }
  return BRIDGE_TOKEN_ENDPOINT_PREFIXES.some((prefix) =>
    key.startsWith(prefix),
  );
}

/**
 * Extract an HTTP status code from an unknown thrown error.
 *
 * Reads, in order of preference:
 * - `statusCode` — {@link import('@/lib/errors').ChatSDKError} (shared `fetcher`,
 *   `mutationFetcherWithSchema`).
 * - `status` — the shared `FetchError` / `throwOnFetchError` seam and
 *   `fetcherWithSchema` (bespoke read fetchers throw via these).
 * - `code` — a `${type}:${surface}` ChatSDKError code string, mapped back to a
 *   status. This is defense-in-depth so any error carrying a serialized code is
 *   still recognized even when no numeric status survived.
 *
 * Returns `undefined` when no status can be determined.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === 'object') {
    const record = error as {
      statusCode?: unknown;
      status?: unknown;
      code?: unknown;
    };
    if (typeof record.statusCode === 'number') {
      return record.statusCode;
    }
    if (typeof record.status === 'number') {
      return record.status;
    }
    if (typeof record.code === 'string') {
      const [type] = record.code.split(':');
      if (type === 'unauthorized') {
        return 401;
      }
      if (type === 'forbidden' || type === 'organization_locked') {
        return 403;
      }
    }
  }
  return undefined;
}

/**
 * A 401 (unauthorized) means the caller has no valid session, and a 403
 * (forbidden) means the session lacks access to this resource. Neither will
 * succeed on retry, so SWR should treat both as terminal instead of entering an
 * exponential-backoff retry loop.
 */
export function isTerminalAuthError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return status === 401 || status === 403;
}

/**
 * Only a 401 indicates the session itself is gone. A 403 means the user IS
 * authenticated but lacks access to a specific resource, which must not drop
 * them to the login screen.
 */
export function isUnauthorizedError(error: unknown): boolean {
  return getErrorStatus(error) === 401;
}

/** Guards against a burst of concurrent 401s triggering multiple recoveries. */
let isRecoveringDeadSession = false;

/**
 * Server endpoint that expires the auth cookies and redirects to login. See
 * `app/api/auth/recover-session/route.ts`.
 */
export const RECOVER_SESSION_URL = '/api/auth/recover-session';

/**
 * Global recovery for a dead/absent app session: clear the stale better-auth
 * state and hard-navigate to the login screen. A hard navigation (rather than a
 * client-side route change) is intentional — it forces a fresh server-side
 * `auth()` check and unfreezes a plugin webview that was stuck rendering zombie
 * logged-in UI while every API call returned 401 (ENG-1242).
 *
 * Recovery runs when the session is *already* dead, so the client `signOut()`
 * network call usually fails — leaving the stale (HttpOnly, JS-unclearable)
 * session cookie in place. Signing in while the browser still presents a dead
 * token is noticeably slower than a clean login, so we always finish through
 * {@link RECOVER_SESSION_URL}, which expires the cookies server-side before
 * landing on login. `signOut()` is still attempted first so a *live* session is
 * revoked server-side; its failure is expected and non-fatal.
 *
 * Idempotent within a session: the first call wins, later ones are ignored.
 */
export async function recoverDeadSession(): Promise<void> {
  if (typeof window === 'undefined') {
    return;
  }
  if (isRecoveringDeadSession) {
    return;
  }
  if (isRecoverySkippedPathname(window.location.pathname)) {
    return;
  }

  isRecoveringDeadSession = true;
  try {
    await signOut();
  } catch (error) {
    console.error('[session-expiry] Failed to clear stale session:', error);
  } finally {
    window.location.assign(RECOVER_SESSION_URL);
  }
}
